import React, { useEffect, useRef, useState } from 'react';
import { Camera, UserCheck, MessageCircle, CalendarRange } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { familyTranslations } from '../data/translations';

const icons = [Camera, UserCheck, MessageCircle, CalendarRange];

const FeaturesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const t = familyTranslations[lang].featuresData;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#5B4FE9]">
            {t.label}
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl md:text-[44px] font-bold text-gray-900 leading-[1.15] tracking-tight"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t.title}
          </h2>
          <p className="mt-5 text-lg text-gray-500 leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {t.features.map((feature, i) => {
            const Icon = icons[i];
            return (
              <div
                key={i}
                className={`group p-7 rounded-2xl border border-gray-100 bg-white hover:shadow-xl hover:border-gray-200 hover:-translate-y-1 transition-all duration-500 ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${200 + i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center mb-5 group-hover:bg-[#5B4FE9]/10 transition-colors duration-300">
                  <Icon className="w-5 h-5 text-[#5B4FE9]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">{feature.title}</h3>
                <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
